/**
 * Parent-Pickable App Skins
 *
 * 8 color presets a parent can choose in Settings → Appearance. Each preset
 * is a full set of `--color-theme-*` CSS variable values applied by AppShell
 * onto <html>. The token shape matches `ShopThemeConfig.colors` so the same
 * application path serves both systems, but skins are intentionally simpler
 * (no particles, no confetti) since they're a baseline appearance choice
 * rather than a reward.
 *
 * Order of precedence in AppShell:
 *   1. Equipped shop theme (only when VIRTUAL_REWARD_ECONOMY_ENABLED is on)
 *   2. Parent-picked appSkin from settingsStore (this file)
 *   3. CSS @theme defaults in src/index.css (== the "blue" skin)
 *
 * Contrast bar: every skin keeps gradient-from/gradient-to inside a single
 * light band (no dark mid-stops) so the existing gray-700 text-secondary
 * token stays WCAG-AA readable across all 8 skins. The dark-blue skin is the
 * exception — it ships with light text tokens.
 */

export type AppSkinId =
  | 'blue'
  | 'green'
  | 'yellow'
  | 'purple'
  | 'pink'
  | 'orange'
  | 'red'
  | 'dark-blue';

export interface AppSkinColors {
  primary: string;
  primaryLight: string;
  secondary: string;
  secondaryLight: string;
  gradientFrom: string;
  gradientTo: string;
  topBarBg: string;
  topBarBorder: string;
  navBg: string;
  xpBarFrom: string;
  xpBarTo: string;
  progressRing: string;
  levelUpFrom: string;
  levelUpTo: string;
  cardAccent: string;
  cardAccentBg: string;
  textPrimary: string;
  textSecondary: string;
  surfaceBg: string;
  surfaceBorder: string;
}

export interface AppSkinPreset {
  id: AppSkinId;
  name: string;
  emoji: string;
  /** Swatch color shown in the picker chip. */
  swatch: string;
  colors: AppSkinColors;
}

// ─── Shared values ───────────────────────────────────────────────────────────

const LIGHT_TEXT = {
  textPrimary: '#111827',   // gray-900
  textSecondary: '#374151', // gray-700 — keeps muted labels WCAG-AA
};

const DARK_TEXT = {
  textPrimary: '#f3f4f6',   // gray-100
  textSecondary: '#d1d5db', // gray-300 — readable on deep navy
};

const COMMON_LIGHT = {
  topBarBg: 'rgba(255,255,255,0.85)',
  topBarBorder: '#e5e7eb',
  navBg: 'rgba(255,255,255,0.92)',
  surfaceBg: 'rgba(255,255,255,0.88)',
  surfaceBorder: '#e5e7eb',
  xpBarFrom: '#fbbf24',
  xpBarTo: '#f59e0b',
  secondary: '#f59e0b',
  secondaryLight: '#fef3c7',
};

const COMMON_DARK = {
  topBarBg: 'rgba(15,23,42,0.85)',
  topBarBorder: '#1e293b',
  navBg: 'rgba(15,23,42,0.92)',
  surfaceBg: 'rgba(30,41,59,0.88)',
  surfaceBorder: '#334155',
  xpBarFrom: '#fbbf24',
  xpBarTo: '#f59e0b',
  secondary: '#f59e0b',
  secondaryLight: '#451a03',
};

// ─── Presets ─────────────────────────────────────────────────────────────────

export const APP_SKIN_PRESETS: Record<AppSkinId, AppSkinPreset> = {
  blue: {
    id: 'blue',
    name: 'Blue',
    emoji: '🌊',
    swatch: '#2563eb',
    colors: {
      ...COMMON_LIGHT,
      ...LIGHT_TEXT,
      primary: '#2563eb',
      primaryLight: '#dbeafe',
      gradientFrom: '#e0f2fe', // sky-100
      gradientTo: '#bae6fd',   // sky-200
      progressRing: '#2563eb',
      levelUpFrom: '#2563eb',
      levelUpTo: '#7c3aed',
      cardAccent: '#93c5fd',
      cardAccentBg: '#eff6ff',
    },
  },

  green: {
    id: 'green',
    name: 'Green',
    emoji: '🌿',
    swatch: '#16a34a',
    colors: {
      ...COMMON_LIGHT,
      ...LIGHT_TEXT,
      primary: '#16a34a',
      primaryLight: '#dcfce7',
      gradientFrom: '#dcfce7', // green-100
      gradientTo: '#bbf7d0',   // green-200
      progressRing: '#16a34a',
      levelUpFrom: '#16a34a',
      levelUpTo: '#0d9488',
      cardAccent: '#86efac',
      cardAccentBg: '#f0fdf4',
    },
  },

  yellow: {
    id: 'yellow',
    name: 'Yellow',
    emoji: '☀️',
    swatch: '#d97706',
    colors: {
      ...COMMON_LIGHT,
      ...LIGHT_TEXT,
      primary: '#d97706',           // amber-600 — high contrast for accents
      primaryLight: '#fef3c7',
      gradientFrom: '#fef9c3',      // yellow-100
      gradientTo: '#fde68a',        // amber-200
      progressRing: '#d97706',
      levelUpFrom: '#d97706',
      levelUpTo: '#ea580c',
      cardAccent: '#fcd34d',
      cardAccentBg: '#fffbeb',
    },
  },

  purple: {
    id: 'purple',
    name: 'Purple',
    emoji: '🪻',
    swatch: '#7c3aed',
    colors: {
      ...COMMON_LIGHT,
      ...LIGHT_TEXT,
      primary: '#7c3aed',
      primaryLight: '#ede9fe',
      gradientFrom: '#ede9fe',      // violet-100
      gradientTo: '#ddd6fe',        // violet-200
      progressRing: '#7c3aed',
      levelUpFrom: '#7c3aed',
      levelUpTo: '#c026d3',
      cardAccent: '#c4b5fd',
      cardAccentBg: '#f5f3ff',
    },
  },

  pink: {
    id: 'pink',
    name: 'Pink',
    emoji: '🌸',
    swatch: '#db2777',
    colors: {
      ...COMMON_LIGHT,
      ...LIGHT_TEXT,
      primary: '#db2777',
      primaryLight: '#fce7f3',
      gradientFrom: '#fce7f3',      // pink-100
      gradientTo: '#fbcfe8',        // pink-200
      progressRing: '#db2777',
      levelUpFrom: '#db2777',
      levelUpTo: '#c026d3',
      cardAccent: '#f9a8d4',
      cardAccentBg: '#fdf2f8',
    },
  },

  orange: {
    id: 'orange',
    name: 'Orange',
    emoji: '🍊',
    swatch: '#ea580c',
    colors: {
      ...COMMON_LIGHT,
      ...LIGHT_TEXT,
      primary: '#ea580c',
      primaryLight: '#ffedd5',
      gradientFrom: '#ffedd5',      // orange-100
      gradientTo: '#fed7aa',        // orange-200
      progressRing: '#ea580c',
      levelUpFrom: '#ea580c',
      levelUpTo: '#dc2626',
      cardAccent: '#fdba74',
      cardAccentBg: '#fff7ed',
    },
  },

  red: {
    id: 'red',
    name: 'Red',
    emoji: '🌶️',
    swatch: '#dc2626',
    colors: {
      ...COMMON_LIGHT,
      ...LIGHT_TEXT,
      primary: '#dc2626',
      primaryLight: '#fee2e2',
      gradientFrom: '#fee2e2',      // red-100
      gradientTo: '#fecaca',        // red-200
      progressRing: '#dc2626',
      levelUpFrom: '#dc2626',
      levelUpTo: '#b91c1c',
      cardAccent: '#fca5a5',
      cardAccentBg: '#fef2f2',
    },
  },

  'dark-blue': {
    id: 'dark-blue',
    name: 'Dark Blue',
    emoji: '🌌',
    swatch: '#1e3a8a',
    colors: {
      ...COMMON_DARK,
      ...DARK_TEXT,
      primary: '#60a5fa',
      primaryLight: 'rgba(59,130,246,0.18)',
      gradientFrom: '#0f172a',      // slate-900
      gradientTo: '#1e3a8a',        // blue-900
      progressRing: '#60a5fa',
      levelUpFrom: '#3b82f6',
      levelUpTo: '#8b5cf6',
      cardAccent: '#3b82f6',
      cardAccentBg: 'rgba(59,130,246,0.12)',
    },
  },
};

export const APP_SKIN_ORDER: AppSkinId[] = [
  'blue',
  'green',
  'yellow',
  'purple',
  'pink',
  'orange',
  'red',
  'dark-blue',
];

export const DEFAULT_APP_SKIN: AppSkinId = 'blue';

export function getAppSkin(id: AppSkinId | undefined): AppSkinPreset {
  if (id && APP_SKIN_PRESETS[id]) return APP_SKIN_PRESETS[id];
  return APP_SKIN_PRESETS[DEFAULT_APP_SKIN];
}
